import classNames from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { useTable, useSortBy, usePagination, SortingRule, Cell } from 'react-table'
import SortIcon, { SortState } from './sort-icon'
import TableFooter from './table-footer'
import { PaginationProps } from './table-footer/pagination'
import { DISPLAY_DATE_FORMAT } from '../../../../services/constants'
import { useNavigate } from 'react-router-dom'
import { Button, ColumnHeader, Tag } from '@msaf/core-react'
import { formatDate } from '@msaf/core-common'

export type TableActionArgs = { constant: string } | { elementKey: string }

export type TableActionType = 'transitionAction' | 'deleteAction'

export type TableAction = {
  label: string
  type: TableActionType
  handler?: (cell: any) => void
  actionPermissions?: Array<string>
  args?: Array<TableActionArgs>
}

export type TableSortByProps = {
  orderColumn: string
  orderDirection?: 'asc' | 'desc'
}

export interface TableProps extends PaginationProps {
  className?: string
  columnHeaders: Array<ColumnHeader>
  tableData: Record<string, string | number | boolean>[]
  onRowClick?: Omit<TableAction, 'handler' | 'actionPermissions'>
  customActions?: { [key: string]: (...args: any[]) => void }
  defaultSortBy: TableSortByProps
  setSortColumn: (orderColumn: string, orderDirection: 'asc' | 'desc') => void
  currentUserPermissions?: Array<string>
  pageSize?: number
  dateFormat?: string
}

export default function Table({
  className,
  columnHeaders,
  tableData,
  onRowClick,
  customActions,
  setSortColumn,
  defaultSortBy,
  currentUserPermissions,
  currentPage,
  maxPageLinksVisible,
  setPage,
  totalResults,
  totalPages,
  showResultsCount,
  resultNameSingular,
  resultNamePlural,
  pageSize = 10,
  dateFormat = DISPLAY_DATE_FORMAT,
}: TableProps) {
  const navigate = useNavigate()
  const [visibleColumns, setVisibleColumns] = useState<Array<ColumnHeader>>([])
  const [visibleActions, setVisibleActions] = useState<Array<TableAction>>([])

  const handleTableAction = (e: React.MouseEvent<any, MouseEvent>, cell: Cell, action: TableAction) => {
    e.stopPropagation()
    const args: Array<string> = []
    action.args?.forEach((arg: TableActionArgs) => {
      if ('constant' in arg && arg.constant !== undefined) {
        args.push(arg.constant)
      }
      if ('elementKey' in arg && arg.elementKey !== undefined && cell?.row?.values) {
        args.push(cell.row.values[arg.elementKey])
      }
    })

    const type = action.type

    if ('transitionAction' === type) {
      navigate(`/${args.join('/')}`)
    } else if (customActions && customActions[type]) {
      customActions[type](...args)
    } else {
      const error = `attempted to run action.${type}(` + [...args].join(' ') + ')'
      throw new Error(error)
    }
  }

  const handleRowClick = (e: any, cell: Cell) => {
    if (onRowClick) {
      handleTableAction(e, cell, onRowClick)
    }
  }

  useEffect(() => {
    const getActionsWithPermissions = (actions: Array<TableAction>) => {
      return actions.filter(({ actionPermissions }) => {
        // Check action permission against current user permissions, if present
        // Otherwise don't return actions
        if (!actionPermissions) {
          return true
        } else if (currentUserPermissions) {
          return currentUserPermissions?.some((perm) => actionPermissions.includes(perm))
        } else {
          return false
        }
      })
    }

    const filteredColumns: Array<ColumnHeader> = columnHeaders.filter((col: ColumnHeader) => {
      if (col.type === 'actions-no-data' && Array.isArray(col.actions)) {
        const actions = getActionsWithPermissions(col.actions)
        setVisibleActions(actions)
        return !!actions?.length
      }
      return true
    })
    setVisibleColumns(filteredColumns)
  }, [columnHeaders, currentUserPermissions])

  const useTableDefaults = {
    columns: visibleColumns.map((col: ColumnHeader) => {
      return {
        Header: col.columnHeading,
        accessor: col.viewColumn ?? col.elementKey,
        disableSortBy: !col.sortable,
        sortDescFirst: true,
        type: col.type,
        Cell: (cell: Cell) => {
          switch (col.type) {
            case 'actions-no-data':
              return (
                Array.isArray(visibleActions) &&
                visibleActions.map((action: any, index: number) => (
                  <Button
                    label={action.label}
                    key={`${col.elementKey}-${index}`}
                    onClick={(e) => handleTableAction(e, cell, action)}
                    buttonStyle='text-action'
                    className='c-table__action'
                  />
                ))
              )
            case 'date':
              return formatDate(cell.value, dateFormat)
            default:
              // TODO: add modifier class for Tag to remove margin bottom, remove nested style from table scss
              return col.type === 'tag' ? <Tag label={cell.value} /> : cell.value
          }
        },
      }
    }),
    data: tableData,
    manualPagination: true,
    manualSortBy: true,
    disableMultiSort: true,
    pageCount: 1,
    autoResetPage: false,
    autoResetHiddenColumns: false,
    autoResetSortBy: false,
    disableSortRemove: true,
    initialState: {
      pageIndex: 0,
      // Results to show per page
      pageSize,
      sortBy: useMemo(
        () => [
          {
            id: defaultSortBy.orderColumn,
            desc: defaultSortBy.orderDirection === 'desc' ? true : false,
          },
        ],
        [defaultSortBy],
      ),
    },
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { sortBy },
    setHiddenColumns,
  } = useTable(useTableDefaults as any, useSortBy, usePagination)

  useEffect(() => {
    if (columnHeaders) {
      setHiddenColumns(() =>
        (columnHeaders as Array<ColumnHeader>).filter((col) => col.type === 'hidden').map((col) => col.elementKey),
      )
    }
  }, [columnHeaders, setHiddenColumns])

  useEffect(() => {
    const [sortData]: SortingRule<object>[] = sortBy
    sortData && setSortColumn(sortData.id, sortData.desc === true ? 'desc' : 'asc')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy])

  const containerClasses = classNames('c-table', className)

  return (
    <div className={containerClasses}>
      <div className='c-table__container'>
        <table className='c-table__table-element' {...getTableProps()}>
          <thead className='c-table__head'>
            {headerGroups.map((headerGroup) => (
              <tr className='c-table__row c-table__row--header' {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((header: any) => {
                  const headerClasses = classNames('c-table__cell', 'c-table__cell--header', {
                    'c-table__cell--active-header': header.isSorted,
                    'c-table__cell--actions': header.type === 'actions-no-data',
                  })

                  let sortState = SortState.inactive
                  if (header.isSortedDesc !== undefined) {
                    sortState = header.isSortedDesc ? SortState.desc : SortState.asc
                  }

                  return (
                    <th key={header.id} className={headerClasses} scope='col'>
                      {/* If column sortable, make label button element for keyboard nav/a11y etc */}
                      {header.disableSortBy ? (
                        <span
                          className='c-table__header-label'
                          {...header.getHeaderProps(header.getSortByToggleProps())}
                        >
                          <span className='c-table__label-text'>{header.render('Header')}</span>
                        </span>
                      ) : (
                        <button
                          className='c-table__header-label'
                          {...header.getHeaderProps(header.getSortByToggleProps())}
                        >
                          <span className='c-table__label-text'>{header.render('Header')}</span>
                          <SortIcon sortState={sortState} />
                        </button>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody className='c-table__body' {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row)

              return (
                <tr key={row.id} className='c-table__row c-table__row--content' {...row.getRowProps()}>
                  {row.cells.map((cell: any, i: number) => {
                    const cellClasses = classNames('c-table__cell', 'c-table__cell--content', {
                      'c-table__cell--actions': cell.column.type === 'actions-no-data',
                    })

                    return (
                      <td
                        key={cell.value}
                        className={cellClasses}
                        {...cell.getCellProps()}
                        onClick={(e) => handleRowClick(e, cell)}
                      >
                        {/* Make first item in row a button to allow for keyboard navigation (CSS used to display normally/handle row focus state)  */}
                        {i === 0 && <button className='c-table__row-click-btn'>{cell.render('Cell')}</button>}
                        {i !== 0 && cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination, result count and (conditional) 'Go to page' controls: */}
      <TableFooter
        currentPage={currentPage}
        setPage={setPage}
        totalResults={totalResults}
        totalPages={totalPages}
        maxPageLinksVisible={maxPageLinksVisible}
        resultNameSingular={resultNameSingular}
        resultNamePlural={resultNamePlural}
        showResultsCount={showResultsCount}
      />
    </div>
  )
}
